import React, { useEffect, useMemo, useState } from 'react';
import { FiAlertTriangle, FiArrowDownLeft, FiArrowUpRight, FiBriefcase, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import {
  Badge,
  Card,
  DataTable,
  EmptyState,
  IconButton,
  PageHeader,
  Select,
  Skeleton,
  StatTile,
  cn,
} from '../components/ui';
import { getWhatsAppUsage, whatsAppBudget, whatsAppCost } from '../servicesV2/whatsappService';

const monthName = (month) =>
  new Date(`${month}-01T00:00:00Z`).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

const formatDateTime = (iso) =>
  iso
    ? new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    : '—';

function MonthSwitch({ months, value, onChange }) {
  return (
    <div className="inline-flex rounded-md border border-line bg-surface p-0.5">
      {months.map((month) => (
        <button
          key={month.id}
          type="button"
          onClick={() => onChange(month.id)}
          className={cn(
            'h-7 px-2.5 rounded text-caption font-medium capitalize transition-colors',
            value === month.id ? 'bg-brand-50 text-brand-700' : 'text-fg-muted hover:text-fg',
          )}
        >
          {month.label}
        </button>
      ))}
    </div>
  );
}

/**
 * WhatsApp — messages exchanged on Betterfly's number, per client and per person, with the
 * estimated Meta cost: every message Betterfly sends is billed from 01/10/2026, received ones
 * are free.
 */
function WhatsAppDashboard() {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [period, setPeriod] = useState('current');
  const [client, setClient] = useState('');

  const load = () => {
    setLoading(true);
    setFailed(false);
    getWhatsAppUsage()
      .then(setUsage, () => setFailed(true))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const tenants = useMemo(() => usage?.tenants || [], [usage]);
  const unreadable = tenants.filter((tenant) => tenant.current == null);

  // One row per person and client: the same person can use WhatsApp for two companies.
  const rows = useMemo(
    () =>
      tenants
        .filter((tenant) => !client || tenant.key === client)
        .flatMap((tenant) =>
          tenant.users.map((user) => ({
            id: `${tenant.key}:${user.userId ?? 'deleted'}`,
            clientName: tenant.name,
            clientKey: tenant.key,
            name: user.name || 'Compte supprimé',
            email: user.email,
            received: user[period].received,
            sent: user[period].sent,
            limitDays: user[period].limitDays || 0,
            lastAt: user.lastAt,
          })),
        )
        .filter((row) => row.received + row.sent > 0),
    [tenants, client, period],
  );

  const totals = rows.reduce(
    (sum, row) => ({ received: sum.received + row.received, sent: sum.sent + row.sent }),
    { received: 0, sent: 0 },
  );
  const activeClients = new Set(rows.map((row) => row.clientKey)).size;

  const columns = [
    {
      id: 'client',
      header: 'Client',
      accessor: 'clientName',
      sortable: true,
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-fg">{row.clientName}</p>
          <p className="truncate font-mono text-caption text-fg-subtle">{row.clientKey}</p>
        </div>
      ),
    },
    {
      id: 'user',
      header: 'Utilisateur',
      accessor: 'name',
      sortable: true,
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-fg">{row.name}</p>
          {row.email && <p className="truncate text-caption text-fg-subtle">{row.email}</p>}
        </div>
      ),
    },
    {
      id: 'received',
      header: 'Reçus',
      accessor: 'received',
      align: 'right',
      sortable: true,
      cell: (row) => <span className="tabular-nums text-fg">{row.received}</span>,
    },
    {
      id: 'sent',
      header: 'Envoyés',
      accessor: 'sent',
      align: 'right',
      sortable: true,
      cell: (row) => <span className="tabular-nums text-fg">{row.sent}</span>,
    },
    {
      id: 'cost',
      header: 'Coût estimé',
      accessor: 'sent',
      align: 'right',
      cell: (row) => <span className="tabular-nums text-fg-muted">{whatsAppCost(row.sent, usage)} $</span>,
    },
    {
      id: 'limitDays',
      header: 'Limite atteinte',
      accessor: 'limitDays',
      align: 'right',
      sortable: true,
      cell: (row) =>
        row.limitDays > 0 ? (
          <Badge variant="warning">
            {row.limitDays} jour{row.limitDays > 1 ? 's' : ''}
          </Badge>
        ) : (
          <span className="text-fg-subtle">—</span>
        ),
    },
    {
      id: 'lastAt',
      header: 'Dernier échange',
      accessor: 'lastAt',
      align: 'right',
      sortable: true,
      cell: (row) => <span className="tabular-nums text-fg-muted">{formatDateTime(row.lastAt)}</span>,
    },
  ];

  const months = usage
    ? [
        { id: 'current', label: monthName(usage.currentMonth) },
        { id: 'previous', label: monthName(usage.previousMonth) },
      ]
    : [];

  const header = (
    <PageHeader
      title="WhatsApp"
      description={`Messages échangés sur le numéro Betterfly, par client et par utilisateur. Depuis le 01/10/2026, Meta facture chaque message envoyé par Betterfly (${usage ? usage.unitPriceUsd : '0.023'} $ au Maroc) ; les messages reçus sont gratuits. Coût estimé : la facture Meta fait foi.`}
      meta={
        unreadable.length > 0 && (
          <Badge variant="warning" dot>
            {unreadable.length} client{unreadable.length > 1 ? 's' : ''} non lisible
            {unreadable.length > 1 ? 's' : ''} : {unreadable.map((tenant) => tenant.name).join(', ')}
          </Badge>
        )
      }
      actions={
        <div className="flex items-center gap-2">
          {usage && <MonthSwitch months={months} value={period} onChange={setPeriod} />}
          <IconButton variant="subtle" size="md" tooltip="Rafraîchir" onClick={load} disabled={loading}>
            <FiRefreshCw className={loading ? 'animate-spin' : ''} />
          </IconButton>
        </div>
      }
    />
  );

  if (loading && !usage) {
    return (
      <>
        {header}
        <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-32 rounded-lg" />
          ))}
        </section>
      </>
    );
  }

  if (failed && !usage) {
    return (
      <>
        {header}
        <EmptyState
          variant="danger"
          icon={<FiAlertTriangle size={24} />}
          title="Compteur indisponible"
          description="Les messages WhatsApp n'ont pas pu être lus. Réessayer avec le bouton Rafraîchir."
        />
      </>
    );
  }

  return (
    <>
      {header}

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatTile
          icon={<FiArrowDownLeft size={18} />}
          label="Messages reçus"
          value={totals.received}
          tone="info"
          hint="envoyés par les utilisateurs"
        />
        <StatTile
          icon={<FiArrowUpRight size={18} />}
          label="Messages envoyés"
          value={totals.sent}
          tone="success"
          hint={`réponses de Betterfly — ~${whatsAppCost(totals.sent, usage)} $ estimés`}
        />
        <StatTile
          icon={<FiUsers size={18} />}
          label="Utilisateurs actifs"
          value={rows.length}
          tone="brand"
          hint="au moins un message dans le mois"
        />
        <StatTile
          icon={<FiBriefcase size={18} />}
          label="Clients actifs"
          value={activeClients}
          tone="neutral"
          hint={`sur ${tenants.length} client${tenants.length > 1 ? 's' : ''} avec WhatsApp`}
        />
      </section>

      {period === 'current' && tenants.some((tenant) => tenant.current != null) && (
        <Card padding="none" className="mt-5">
          <div className="border-b border-line px-4 py-3">
            <h3 className="text-body-sm font-semibold text-fg">Budget du mois par client</h3>
            <p className="text-caption text-fg-subtle">
              Au-delà, WhatsApp invite poliment à poursuivre sur l'écran jusqu'au 1er du mois suivant. Budget réglé
              dans la fiche client (vide : 20 $).
            </p>
          </div>
          <ul className="divide-y divide-line">
            {tenants
              .filter((tenant) => tenant.current != null)
              .map((tenant) => {
                const budget = whatsAppBudget(tenant, usage);
                const share = Math.min(100, (Number(budget.spent) / Math.max(tenant.budgetUsd, 0.01)) * 100);
                return (
                  <li key={tenant.key} className="flex items-center gap-4 px-4 py-2.5">
                    <p className="w-40 truncate text-body-sm text-fg">{tenant.name}</p>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-ink-100">
                      <div
                        className={cn('h-full rounded-full', budget.reached ? 'bg-warning-500' : 'bg-brand-500')}
                        style={{ width: `${share}%` }}
                      />
                    </div>
                    <p className="w-32 text-right tabular-nums text-body-sm text-fg">
                      {budget.spent} $ / {budget.budget} $
                    </p>
                    <div className="w-28 text-right">
                      {budget.reached ? (
                        <Badge variant="warning">Budget atteint</Badge>
                      ) : (
                        <span className="text-caption text-fg-subtle">{Math.round(share)} %</span>
                      )}
                    </div>
                  </li>
                );
              })}
          </ul>
        </Card>
      )}

      {usage.unlinked && (
        <Card padding="none" className="mt-5">
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <h3 className="text-body-sm font-semibold text-fg">Numéros sans accès</h3>
              <p className="text-caption text-fg-subtle">
                Numéros sans compte Betterfly ou dont l'entreprise n'a pas WhatsApp : une seule réponse par 24 h,
                les autres messages restent sans réponse (non facturés).
              </p>
            </div>
            <div className="flex items-center gap-6 tabular-nums">
              <div className="text-right">
                <p className="text-body-sm font-semibold text-fg">{usage.unlinked[period].phones}</p>
                <p className="text-caption text-fg-subtle">numéros</p>
              </div>
              <div className="text-right">
                <p className="text-body-sm font-semibold text-fg">{usage.unlinked[period].notices}</p>
                <p className="text-caption text-fg-subtle">
                  réponses — ~{whatsAppCost(usage.unlinked[period].notices, usage)} $
                </p>
              </div>
              <div className="text-right">
                <p className="text-body-sm font-semibold text-fg">{usage.unlinked[period].ignored}</p>
                <p className="text-caption text-fg-subtle">messages ignorés</p>
              </div>
            </div>
          </div>
        </Card>
      )}

      <Card padding="none" className="mt-5">
        <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <div className="flex items-center gap-2">
            <FaWhatsapp size={14} className="text-fg-subtle" />
            <h3 className="text-body-sm font-semibold text-fg">Détail par utilisateur</h3>
          </div>
          <div className="w-56">
            <Select size="sm" value={client} onChange={(event) => setClient(event.target.value)} aria-label="Client">
              <option value="">Tous les clients</option>
              {tenants.map((tenant) => (
                <option key={tenant.key} value={tenant.key}>
                  {tenant.name}
                </option>
              ))}
            </Select>
          </div>
        </div>
        <DataTable
          columns={columns}
          data={rows}
          rowKey="id"
          initialSort={{ columnId: 'sent', direction: 'desc' }}
          pageSize={25}
          isFiltered={!!client}
          empty={
            <EmptyState
              icon={<FaWhatsapp size={24} />}
              title="Aucun message ce mois-ci"
              description="Aucun utilisateur n'a encore échangé sur WhatsApp pendant cette période."
            />
          }
          emptyFiltered={
            <EmptyState
              title="Aucun message pour ce client"
              description="Ce client n'a pas encore échangé sur WhatsApp pendant cette période."
            />
          }
        />
      </Card>
    </>
  );
}

export default WhatsAppDashboard;
